/**
 * httpClient.js — Axios instance for the TapstarQR Django REST API
 * =================================================================
 * - Baseurl auto-resolved: proxied via Vite in dev, same-origin in prod
 * - Automatically attaches the Django CSRF token on every mutating request
 * - On 401 responses, redirects the user to /login
 * - On 429 responses (rate limited), shows a toast with retry_after seconds
 *   and rejects the promise so callers can still react if needed.
 */

import axios from 'axios';
import toast from 'react-hot-toast';
import i18n from 'i18next';
// authRedirect es un módulo puro y sin imports justamente para poder usarlo
// desde aquí sin montar un ciclo.
import { buildLoginUrl, isPublicPath } from '@/lib/authRedirect';

// ---------------------------------------------------------------------------
// Django CSRF helper
// ---------------------------------------------------------------------------
function getCsrfToken() {
  const name = 'csrftoken';
  const cookies = document.cookie.split(';');
  for (let c of cookies) {
    const trimmed = c.trim();
    if (trimmed.startsWith(name + '=')) {
      return decodeURIComponent(trimmed.slice(name.length + 1));
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
const httpClient = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,       // send session cookie
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 15000,
});

// Attach CSRF token to non-safe methods
httpClient.interceptors.request.use((config) => {
  const safeMethods = ['GET', 'HEAD', 'OPTIONS', 'TRACE'];
  if (!safeMethods.includes(config.method?.toUpperCase())) {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers['X-CSRFToken'] = csrf;
    }
  }
  return config;
});

// Global 429 throttle: agrupa toasts duplicados con un toastId estable por
// scope, así un componente que dispara N requests en burst solo enseña 1 toast.
let lastThrottleToastAt = 0;
function showThrottleToast(retryAfter, scope) {
  const now = Date.now();
  // Anti-spam: como mucho 1 toast cada 2s aunque caigan 10 peticiones a la vez.
  if (now - lastThrottleToastAt < 2000) return;
  lastThrottleToastAt = now;

  const seconds = Number.isFinite(retryAfter) ? Math.max(1, Math.ceil(retryAfter)) : null;
  const message = seconds
    ? i18n.t('errors.rate_limited', { seconds, defaultValue: `Demasiados intentos. Vuelve a intentarlo en ${seconds}s.` })
    : i18n.t('errors.rate_limited_generic', { defaultValue: 'Demasiados intentos. Inténtalo más tarde.' });

  toast.error(message, { id: `throttle:${scope || 'generic'}`, duration: 5000 });
}

// Una página suele tener varias peticiones en vuelo: si la sesión ha caducado
// las N devuelven 401 a la vez y sin este flag se reasigna `location.href` N
// veces.
let redirigiendoAlLogin = false;

// Handle 401/429 globally
httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    if (status === 401) {
      // Esta es la vía por la que se sale al login en la mayoría de los casos:
      // al abrir un deep-link sin sesión, el /auth/me/ de AuthContext devuelve
      // 401 y llega aquí ANTES de que el guard de App.jsx se ejecute. Por eso
      // el destino se conserva también aquí y no solo en el guard: si no, un
      // enlace de correo a /Reviews?reviewId=123 acaba en /login pelado.
      //
      // La lista de rutas públicas es la MISMA que usa el guard (isPublicPath):
      // en una página pública un 401 es lo normal —el /auth/me/ de la landing,
      // del escaneo /redirect, del LinkTree /en1/:id— y no debe expulsar a
      // nadie. La comparación de antes era sensible a mayúsculas, no toleraba
      // la barra final y solo cubría tres rutas.
      if (!redirigiendoAlLogin && !isPublicPath(window.location.pathname)) {
        redirigiendoAlLogin = true;
        window.location.href = buildLoginUrl(window.location);
      }
    } else if (status === 429) {
      const data = error.response?.data || {};
      // Solo el throttle DRF (que setea `scope` + `retry_after` vía nuestro
      // exception handler en qr_project) dispara el toast global. Las cuotas
      // aplicativas (AIQuotaExceeded, FreeManualReplyQuotaExceeded, ...) usan
      // 429 con `code`/`limit`/`used` pero sin `scope`, y las gestiona el
      // mutation.onError del componente (abrir modal, toast contextual).
      if (data.scope) {
        showThrottleToast(data.retry_after, data.scope);
      }
    }
    return Promise.reject(error);
  }
);

export default httpClient;
